// Файл: frontend/src/api/apiService.js

import api from './axiosConfig.js';
import { startOfDay, endOfDay } from 'date-fns';

export const ONLINE_TTL_MS = Number(process.env.REACT_APP_ONLINE_TTL_MS || 300000); // 5 мин по умолчанию

// ----------------------------
// Вспомогательные функции
// ----------------------------
const pickId = (x) =>
  x && typeof x === 'object'
    ? (x._id || x.id || x.value || x.userId || x.representativeId || x.repId || '')
    : (x ?? '');

/**
 * Нормализация идентификатора представителя в параметры запроса.
 * Создаём сразу несколько синонимов (repId/representativeId/userId) —
 * бэкенд возьмёт нужный.
 */
function normalizeRepParams(p = {}) {
  const raw =
    p.repId ??
    p.representativeId ??
    p.representative ??
    p.userId ??
    (p.rep && pickId(p.rep)) ??
    (p.user && pickId(p.user)) ??
    '';

  // Может прийти один сотрудник или список (мультивыбор медпредов):
  // массив, строка "id1,id2" или объект — приводим к "id1,id2"
  const ids = [...new Set(
    (Array.isArray(raw) ? raw : String(pickId(raw) ?? '').split(','))
      .map(v => String(pickId(v) ?? '').trim())
      .filter(Boolean)
  )];

  const out = {};
  if (ids.length) {
    const value = ids.join(',');
    out.repId = value;
    out.representativeId = value;
    out.userId = value;
  }
  return out;
}

/**
 * Нормализация идентификатора продукта.
 * Если в параметрах передано название (строка не-ObjectId), НЕ отправляем его —
 * иначе бэкенд может вернуть 500 (Cast to ObjectId failed).
 */
function normalizeProductParams(p = {}) {
  // Берем значение из любого возможного поля
  let raw =
    p.productId ??
    p.medicationId ??
    p.product ??
    p.medication ??
    '';

  // Пытаемся извлечь ._id если передали объект, иначе берем как есть
  raw = pickId(raw);

  const out = {};
  
  // Если значение есть (хоть какое-то) — отправляем его!
  if (raw) {
    const val = String(raw).trim(); // Убираем пробелы, но не проверяем формат
    out.medication = val;
    out.products = val; // Дублируем для надежности
    out.productId = val;
  }
  
  return out;
}

/**
 * Нормализация дат:
 * - поддержка params.date -> [startDate, endDate] по границам дня
 * - пропускаем пустые значения
 */
function normalizeDateParams(p = {}) {
  const out = {};
  if (p.date && !p.startDate && !p.endDate) {
    out.startDate = startOfDay(p.date).toISOString();
    out.endDate = endOfDay(p.date).toISOString();
  } else {
    if (p.startDate) out.startDate = p.startDate;
    if (p.endDate) out.endDate = p.endDate;
  }
  return out;
}

/**
 * Общая нормализация параметров для /visits
 */
export function normalizeVisitsParams(p = {}) {
  const out = {
    ...normalizeDateParams(p),
    ...normalizeRepParams(p),
    ...normalizeProductParams(p),
  };
  // Пробрасываем дополнительные фильтры, которые могут потребоваться на фронте:
  // status (completed/cancelled/etc.), unplanned (boolean or '1'), missed (boolean), type (doctor/pharmacy/event/dayoff)
  if (p.status !== undefined && p.status !== null) out.status = p.status;
  if (p.unplanned !== undefined && p.unplanned !== null) out.unplanned = p.unplanned;
  if (p.missed !== undefined && p.missed !== null) out.missed = p.missed;
  if (p.type !== undefined && p.type !== null) out.type = p.type;
  // doctorId — история визитов в карточке врача. Без этого параметр терялся
  // здесь, и карточка получала вообще все визиты, а не визиты к этому врачу.
  const doctorId = pickId(p.doctorId ?? p.doctor ?? '');
  if (doctorId) out.doctorId = String(doctorId);
  return out;
}

// ----------------------------
// Auth (Аутентификация)
// ----------------------------
export const loginUser = (credentials) => api.post('auth/login', credentials);
export const registerUser = (userData) => api.post('auth/register', userData);

// ----------------------------
// Users (Пользователи)
// ----------------------------
export const getUsers = () => api.get('users');

// ----------------------------
// Profile (Профиль)
// ----------------------------
export const getProfile = () => api.get('profile');
export const updateProfile = (profileData) => api.put('profile', profileData);
export const updatePassword = (passwordData) => api.put('profile/password', passwordData);
export const uploadAvatar = (formData) =>
  api.post('profile/avatar', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// ----------------------------
// Doctors (Врачи)
// ----------------------------
export const getDoctors = () => api.get('doctors');
export const createDoctor = (doctorData) => api.post('doctors', doctorData);
export const updateDoctor = (doctorId, doctorData) => api.put(`doctors/${doctorId}`, doctorData);
export const deleteDoctor = (doctorId) => api.delete(`doctors/${doctorId}`);

// ----------------------------
// Pharmacies (Аптеки)
// ----------------------------
export const getPharmacies = () => api.get('pharmacies');
export const createPharmacy = (data) => api.post('pharmacies', data);
export const updatePharmacy = (id, data) => api.put(`pharmacies/${id}`, data);
export const deletePharmacy = (id) => api.delete(`pharmacies/${id}`);
export const importPharmacies = (formData) =>
  api.post('pharmacies/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// ----------------------------
// Products (Продукты)
// ----------------------------
export const getProducts = () => api.get('products');

// ----------------------------
// Specialities (Специальности)
// ----------------------------
export const getSpecialities = () => api.get('specialities');
export const createSpeciality = (data) => api.post('specialities', data);
export const deleteSpeciality = (id) => api.delete(`specialities/${id}`);

// ----------------------------
// Hospitals (Больницы)
// ----------------------------
export const getHospitals = () => api.get('hospitals');
export const createHospital = (data) => api.post('hospitals', data);
export const deleteHospital = (id) => api.delete(`hospitals/${id}`);

// ----------------------------
// Visits (Визиты)
// ----------------------------
export const getVisits = (params = {}) => {
  // Не мутируем входной объект
  const normalized = normalizeVisitsParams(params);
  return api.get('visits', { params: normalized });
};

export const startVisit = (visitData) => api.post('visits/start', visitData);
export const endVisit = (visitId, locationData) => api.post(`visits/end/${visitId}`, locationData);
export const addTextReport = (visitId, reportData) => api.post(`visits/report/${visitId}`, reportData);
export const uploadAudioReport = (visitId, formData) =>
  api.post(`visits/report/audio/${visitId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000, // 2 минуты — длинные аудио могут загружаться долго
  });

// Get visit plans (planned visits for calendar)
export const getVisitPlans = (params = {}) => api.get('visits/plans', { params });
export const createVisitPlan = (data) => api.post('visits/plan', data);
export const updateVisitPlan = (id, data) => api.put(`visits/plan/${id}`, data);
export const deleteVisitPlan = (id) => api.delete(`visits/plan/${id}`);

// Plan-Fact analysis (план-факт анализ визитов)
export const getPlanFactAnalysis = (params = {}) => api.get('visits/plan-fact', { params });

// ----------------------------
// Events (События в календаре)
// ----------------------------
export const getEvents = (params) => api.get('events', { params });
export const saveEvent = (eventData) =>
  eventData._id ? api.put(`events/${eventData._id}`, eventData) : api.post('events', eventData);
export const deleteEvent = (eventId) => api.delete(`events/${eventId}`);

// ----------------------------
// Notifications (Уведомления)
// ----------------------------
export const getMyNotifications = () => api.get('notifications');
export const markAllNotificationsAsRead = () => api.post('notifications/mark-as-read');
export const deleteAllNotifications = () => api.delete('notifications/all');

// ----------------------------
// Location (Геолокация)
// ----------------------------
export const getLocation = () => api.get('location');

// Последние локации всех репов
export const getAllLastLocations = () => api.get('location/all-reps');

// Алиас для совместимости с мобильным названием
export const getAllRepsLocation = () => getAllLastLocations();

// Трек репа за дату. range = { from, to } (ISO) — точные границы дня
// в часовом поясе пользователя; без range день трактуется бэкендом как UTC-сутки.
export const getRepTrack = (repId, date, range) => {
  if (range?.from && range?.to) {
    return api.get(`location/track/${repId}`, { params: { from: range.from, to: range.to } });
  }
  const d =
    typeof date === 'string'
      ? date
      : date instanceof Date
      ? date.toISOString().slice(0, 10)
      : undefined;
  return api.get(`location/track/${repId}`, { params: d ? { date: d } : undefined });
};

// Дневной пробег всех репов (метры) за период — для списка на живой карте
export const getDailyDistances = (from, to) =>
  api.get('location/daily-distances', { params: { from, to } });

// ----------------------------
// Presence (Онлайн/офлайн)
// ----------------------------

/**
 * Безопасная обёртка: если presence/online отсутствует,
 * считаем "онлайн" по свежести последней локации (TTL).
 */
export const getPresenceOnlineSafe = async () => {
  try {
    const res = await api.get('presence/online');
    const data = res?.data;
    if (data?.online && Array.isArray(data.online)) return new Set(data.online.map(String));
    if (Array.isArray(data)) return new Set(data.map(String));
    return new Set();
  } catch {
    try {
      const locsRes = await getAllLastLocations();
      const arr = Array.isArray(locsRes?.data)
        ? locsRes.data
        : Array.isArray(locsRes)
        ? locsRes
        : [];

      const now = Date.now();
      const online = new Set();

      for (const it of arr) {
        const id = String(
          it?.userId ??
            it?.repId ??
            it?.representativeId ??
            it?.user?._id ??
            it?.user?.id ??
            it?.id ??
            it?._id ??
            ''
        ).trim();
        if (!id) continue;

        const tRaw =
          it?.ts ??
          it?.lastSeen ??
          it?.timestamp ??
          it?.updatedAt ??
          it?.createdAt ??
          it?.time;

        let ts = typeof tRaw === 'number' ? tRaw : Date.parse(tRaw || '');
        // Если пришли секунды — переводим в миллисекунды
        if (Number.isFinite(ts) && ts < 1e12) ts *= 1000;

        if (Number.isFinite(ts) && Math.abs(now - ts) < ONLINE_TTL_MS) {
          online.add(id);
        }
      }
      return online;
    } catch {
      return new Set();
    }
  }
};

// Пинг присутствия по контракту бэка: нужны userId и deviceId
export const postPresencePing = (userId, deviceId = 'web') =>
  api.post('presence/ping', { userId, deviceId });

// ----------------------------
// Inventory (Анализ запасов - старая версия)
// ----------------------------
export const uploadAndAnalyzeInventory = (formData) =>
  api.post('inventory/analyze', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// ----------------------------
// Inventory V2 (Новая версия с сохранением в БД)
// ----------------------------

// Загрузка файла инвентаря
export const uploadInventoryFile = (formData) =>
  api.post('inventory/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// Получение данных дашборда
export const getInventoryDashboard = (filters = {}) =>
  api.post('inventory/dashboard', filters);

// Получение истории загрузок
export const getUploadHistory = () =>
  api.get('inventory/history');

// Активация старой загрузки
export const activateUpload = (uploadId) =>
  api.put(`inventory/activate/${uploadId}`);

// Удаление загрузки
export const deleteUpload = (uploadId) =>
  api.delete(`inventory/upload/${uploadId}`);

// Получение исторических данных для сравнения
export const getHistoricalData = (params) =>
  api.post('inventory/historical', params);

// Экспорт данных инвентаря в Excel
export const exportInventoryToExcel = (filters) =>
  api.post('inventory/export', filters, { responseType: 'blob' });

// Получение списка складов
export const getWarehouses = () =>
  api.get('inventory/warehouses');

// Получение списка поставщиков
export const getSuppliers = () =>
  api.get('inventory/suppliers');

// Получение списка продуктов инвентаря
export const getInventoryProducts = (search = '') =>
  api.get('inventory/products', { params: { search } });

// Получение детальной информации о продукте
export const getProductDetails = (productId) =>
  api.get(`inventory/product/${productId}`);

// Получение ABC анализа
export const getABCAnalysis = (filters) =>
  api.post('inventory/abc-analysis', filters);

// Получение XYZ анализа
export const getXYZAnalysis = (filters) =>
  api.post('inventory/xyz-analysis', filters);

// Получение прогноза
export const getForecast = (params) =>
  api.post('inventory/forecast', params);

// ----------------------------
// Sales (Анализ продаж)
// ----------------------------
export const uploadAndAnalyzeSales = (formData) =>
  api.post('sales/analyze', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// экспорт CSV на клиенте (утилита)
export function exportCsv(filename, rows) {
  const header = Object.keys(rows[0] || {});
  const csv = [
    header.join(','),
    ...rows.map(row => header.map(field => {
      const val = row[field];
      const s = String(val ?? '');
      // экранирование
      if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    }).join(',')),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

